import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import PaperDraggable from "../../../../../../../components/PaperDraggable";
import SelectPlanoContas from "../../../../../../../components/SelectsSimpleApiQuery/SelectPlanoContas";
import { ContaContabilResumida } from "../../../../../../../types/api/geral/pesquisar-plano-contas";
import { IDetalharTituloPagar } from "../../../../../../../types/api/financeiro/detalhar-titulo-pagar";
import SelectBancos from "../../../../../../../components/SelectsSimpleApiQuery/SelectBancos";
import { TBancos } from "../../../../../../../types/api/financeiro/bancos";
import {
  formatarCPFCNPJ,
  permitirApenasNumeros,
} from "../../../../../../../helpers/masks";
import SelectNaturezaRendimento from "../../../../../../../components/SelectsSimpleApiQuery/SelectNaturezaRendimento";
import { IDataNaturezaRendimento } from "../../../../../../../types/api/financeiro/pesquisar-natureza-rendimentos";
import { desformatarCPFCNPJ } from "../../../../../../../helpers/unmask";
import SelectContaCorrente from "../../../../../../../components/SelectsSimpleApiQuery/SelectContaCorrente";
import { TContaCorrente } from "../../../../../../../types/api/financeiro/conta-corrente";

export default function DialogEdit({
  open,
  handleClose,
  titulo,
  codigo,
}: DialogEditProps) {
  const queryClient = useQueryClient();

  const [contaCorrente, setContaCorrente] = useState<TContaCorrente | null>({
    id: `${titulo.publico?.tipo_publico || ""}-${titulo.publico?.codigo_publico || ""
      }-${titulo.banco_pagamento || ""}-${titulo.agencia_pagamento?.split("-")[0] || ""
      }-${titulo.conta_pagamento?.split("-")[0] || ""}`,
    banco: titulo.banco_pagamento ? titulo.banco_pagamento : null,
    agencia: titulo.agencia_pagamento.split("-")[0],
    agencia_digito: titulo.agencia_pagamento.split("-")[1] || "",
    conta: titulo.conta_pagamento.split("-")[0],
    conta_digito: titulo.conta_pagamento.split("-")[1] || "",
    nome: "",
    cpf_cnpj_conta: 0
  });

  const [internFilters, setInternFilters] = useState<TInternFilters>({
    banco_pagamento: titulo.banco_pagamento || null,
    agencia_pagamento: titulo.agencia_pagamento,
    nome_favorecido: titulo.nome_favorecido,
    conta_pagamento: titulo.conta_pagamento,
    forma_pagamento: titulo.forma_pagamento || "Não informado",
    codigo_barras: titulo.codigo_barras || "",
    chave_pix: titulo.chave_pix || "",
    conta_devedora: titulo.conta_devedora
      ? {
        codigo_contabil: titulo.conta_devedora.codigo_contabil,
        nome: titulo.conta_devedora.nome,
        centro_custo: titulo.centro_custo_devedora
          ? {
            id: titulo.centro_custo_devedora.id,
            nome: titulo.centro_custo_devedora.nome,
          }
          : null,
      }
      : null,
    conta_credora: titulo.conta_credora
      ? {
        codigo_contabil: titulo.conta_credora.codigo_contabil,
        nome: titulo.conta_credora.nome,
        centro_custo: titulo.centro_custo_credora
          ? {
            id: titulo.centro_custo_credora.id,
            nome: titulo.centro_custo_credora.nome,
          }
          : null,
      }
      : null,
    natureza_rendimento: titulo.natureza_rendimento
      ? {
        codigo: titulo.natureza_rendimento.codigo,
        nome: titulo.natureza_rendimento.nome,
      }
      : null,
    documento_favorecido: formatarCPFCNPJ(titulo.documento_favorecido),
    tipo_documento_favorecido:
      titulo.tipo_documento_favorecido === ""
        ? "Não informado"
        : titulo.tipo_documento_favorecido,
  });

  //querys e mutations
  const { mutate: atualizarTitulo, isLoading } = useMutation({
    mutationKey: "atualizar-titulo-pagar",
    mutationFn: (input: any) =>
      axiosClient.put(`financeiro/contas-pagar/atualizar`, input),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(`Erro ao atualizar título`, { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(
        "financeiro/contas-pagar/detalhar-titulo-pagar"
      );
      queryClient.invalidateQueries(
        "table-data-grid-financeiro/contas-pagar/pesquisar-titulos-pagar"
      );
      enqueueSnackbar(`Título atualizado com sucesso!`, { variant: "success" });
      handleClose();
    },
  });
  //

  //funçoes manipuladoras
  const handleChangeInputs = (prop: keyof TInternFilters, value: any) => {
    setInternFilters((prevState) => ({ ...prevState, [prop]: value }));
  };

  const handleChangeContaCorrente = (value: TContaCorrente) => {
    setContaCorrente(value);

    setInternFilters((prevState) => ({
      ...prevState,
      banco_pagamento: value.banco || null,
      agencia_pagamento: value.agencia
        ? `${value.agencia}-${value.agencia_digito || ""}`
        : "",
      conta_pagamento: value.conta
        ? `${value.conta}-${value.conta_digito || ""}`
        : "",
    }));
  };

  const handleSubmit = () => {
    const {
      banco_pagamento,
      agencia_pagamento,
      conta_pagamento,
      nome_favorecido,
      forma_pagamento,
      codigo_barras,
      chave_pix,
      conta_devedora,
      conta_credora,
      documento_favorecido,
      tipo_documento_favorecido,
    } = internFilters;

    const params = {
      id_titulo: codigo,
      banco_pagamento_id: banco_pagamento ? banco_pagamento.codigo : null,
      agencia: agencia_pagamento.split("-")[0],
      agencia_digito: agencia_pagamento.split("-")[1] || "",
      conta: conta_pagamento.split("-")[0],
      conta_digito: conta_pagamento.split("-")[1] || "",
      nome_favorecido,
      forma_pagamento,
      codigo_barras: codigo_barras.trim(),
      chave_pix: chave_pix.trim(),
      conta_credora_id: conta_credora
        ? Number(conta_credora.codigo_contabil)
        : null,
      conta_custo_credora_id:
        conta_credora && conta_credora.centro_custo
          ? Number(conta_credora.centro_custo.id)
          : null,
      conta_devedora_id: conta_devedora
        ? Number(conta_devedora.codigo_contabil)
        : null,
      conta_custo_devedora_id:
        conta_devedora && conta_devedora.centro_custo
          ? Number(conta_devedora.centro_custo.id)
          : null,
      natureza_rendimento_id: internFilters.natureza_rendimento
        ? internFilters.natureza_rendimento.codigo
        : null,
      documento_favorecido: desformatarCPFCNPJ(documento_favorecido),
      tipo_documento_favorecido:
        tipo_documento_favorecido === "Não informado"
          ? ""
          : tipo_documento_favorecido,
    };
    atualizarTitulo(params);
  };
  //

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
      fullWidth
      maxWidth={"md"}
      scroll="body"
    >
      <DialogTitle
        style={{ cursor: "move", paddingBottom: 0 }}
        id="draggable-dialog-title"
      >
        Editar título
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} pt={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <SelectContaCorrente
              value={contaCorrente}
              onChange={(value) => handleChangeContaCorrente(value as TContaCorrente)}
              titulo={titulo}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectBancos
              value={internFilters.banco_pagamento}
              onChange={(value) => handleChangeInputs("banco_pagamento", value)}
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Agência"
              variant="outlined"
              value={internFilters.agencia_pagamento}
              onChange={({ target }) =>
                handleChangeInputs("agencia_pagamento", target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 6, md: 3 }}>
            <TextField
              fullWidth
              label="Conta"
              variant="outlined"
              value={internFilters.conta_pagamento}
              onChange={({ target }) =>
                handleChangeInputs("conta_pagamento", target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Nome do favorecido"
              variant="outlined"
              value={internFilters.nome_favorecido}
              onChange={({ target }) =>
                handleChangeInputs("nome_favorecido", target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="select-tipo-documento-favorecido">
                Tipo pessoa
              </InputLabel>
              <Select
                labelId="select-tipo-documento-favorecido"
                value={internFilters.tipo_documento_favorecido}
                label="Tipo pessoa"
                onChange={(e) =>
                  handleChangeInputs(
                    "tipo_documento_favorecido",
                    e.target.value
                  )
                }
              >
                <MenuItem value="Não informado">Não informado</MenuItem>
                <MenuItem value="F">Pessoa física</MenuItem>
                <MenuItem value="J">Pessoa jurídica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Documento do favorecido"
              variant="outlined"
              value={internFilters.documento_favorecido}
              slotProps={{
                htmlInput: {
                  maxLength:
                    internFilters.tipo_documento_favorecido === "J" ||
                      internFilters.tipo_documento_favorecido === "Não informado"
                      ? 16
                      : 14
                }
              }}
              onChange={({ target }) =>
                handleChangeInputs(
                  "documento_favorecido",
                  formatarCPFCNPJ(target.value)
                )
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="select-forma-pagamento">
                Forma de pagamento
              </InputLabel>
              <Select
                labelId="select-forma-pagamento"
                value={internFilters.forma_pagamento}
                label="Forma de pagamento"
                onChange={(e) =>
                  handleChangeInputs("forma_pagamento", e.target.value)
                }
              >
                <MenuItem value="Não informado">Não informado</MenuItem>
                <MenuItem value="DOC/TED/Transferência">
                  DOC/TED/Transferência
                </MenuItem>
                <MenuItem value="Boleto">Boleto</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="DDA">DDA</MenuItem>
                <MenuItem value="Visto">Visto</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Código de barras"
              variant="outlined"
              value={internFilters.codigo_barras}
              onChange={({ target }) =>
                handleChangeInputs(
                  "codigo_barras",
                  permitirApenasNumeros(target.value)
                )
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Chave pix"
              variant="outlined"
              value={internFilters.chave_pix}
              onChange={({ target }) =>
                handleChangeInputs("chave_pix", target.value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectPlanoContas
              inputLabel="Conta devedora"
              value={internFilters.conta_devedora}
              onChangeValue={(value) =>
                handleChangeInputs("conta_devedora", value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <SelectPlanoContas
              inputLabel="Conta credora"
              value={internFilters.conta_credora}
              onChangeValue={(value) =>
                handleChangeInputs("conta_credora", value)
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <SelectNaturezaRendimento
              value={internFilters.natureza_rendimento}
              onChange={(value) =>
                handleChangeInputs("natureza_rendimento", value)
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>

        <Button
          disabled={isLoading}
          variant="contained"
          onClick={() => handleSubmit()}
        >
          {isLoading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
          Editar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface DialogEditProps {
  open: boolean;
  handleClose: () => void;
  titulo: IDetalharTituloPagar;
  codigo: string;
}

type TInternFilters = {
  banco_pagamento: TBancos | null;
  agencia_pagamento: string;
  conta_pagamento: string;
  nome_favorecido: string;
  forma_pagamento: string;
  codigo_barras: string;
  chave_pix: string;
  conta_credora: ContaContabilResumida | null;
  conta_devedora: ContaContabilResumida | null;
  natureza_rendimento: IDataNaturezaRendimento | null;
  tipo_documento_favorecido: string | null;
  documento_favorecido: string;
};
